import React, { useState, useEffect } from "react";

import { AgGridColumn, AgGridReact } from "ag-grid-react";
import Tippy from "@tippyjs/react";
import { cloneDeep, isEmpty, isEqual } from "lodash";
import { Trans, useTranslation } from "react-i18next";
import { SuccessAlert } from "../../utils/Alerts";
import OptionsDropdown from "../../components/Dropdown/OptionsDropdown";
import MeasurementDeleteModal from "../Modals/MeasurementDeleteModal";
import ScansAPI from "../../API/scans.api";
import ScanDetailsModal from "../Modals/ScanDetailsModal";
import { shortDateTime } from "../../utils/DateHelper";
import LoadingOverlay from "../Loaders/LoadingOverlay";
import {
    EXPORT_FORMATS,
    FIELD_TYPES,
    METADATA_COLUMNS,
    MODEL_PROCESSING_TYPES,
    ROLES,
} from "../../utils/constants";
import LogCapture from "../../utils/LogManagement/LogHelper";
import BaseModal from "../Modals/BaseModal";
import SpectrumChart from "../Charts/SpectrumChart";
import { CreateSpectrumPoints } from "../../utils/CreatePoints";
import DropdownButton from "../Buttons/DropdownButton";
import { SPECTRUM_POINTS_BOUNDARY } from "../../utils/constants";
import Pagination from "../Pagination/Pagination";
import { getObject, setObject } from "../../utils/StorageHelpers";
import ExportScansModal from "../Modals/ExportScansModal";
import scansApi from "../../API/scans.api";
import { handleExternalLink } from "../../utils/ExternalLinksHelper";
import VerticalRadioButtons from "../RadioButtons/VerticalRadioButtons";
import { getRoles } from "../../utils/OktaHelpers";
import { isAuthorized } from "../../utils/RolesHelpers";

const CAPTURED_AT_COL = "capturedAt";

const ScansDataTable = ({
    visible,
    data,
    selectedRows,
    setSelectedRows,
    selectedSpectrumPoints,
    setSelectedSpectrumPoints,
    modifiedDataReference,
    // setModifiedDataReference,
    externalDataReference,
    // setExternalDataReference,
    isLoading,
    setIsLoading,
    isWavenumber,
    setIsWavenumber,
    isReflectance,
    setIsReflectance,
    xAxisCheckedReference,
    yAxisCheckedReference,
    setXAxisCheckedReference,
    setYAxisCheckedReference,
    scans,
    getScans,
    mapScanFilter,
    appliedFilter,
    lotsLookup,
    suppliersLookup,
    materialId,
}) => {
    const [, setHeaders] = useState([]);
    const [mixedData, setMixedData] = useState([]);
    const [gridApi, setGridApi] = useState(null);
    const [gridColumnApi, setGridColumnApi] = useState(null);
    const [isDataHidden, setIsDataHidden] = useState(true);
    const [isReferenceHidden, setIsReferenceHidden] = useState(false);
    const [isExternalReferenceHidden, setIsExternalReferenceHidden] =
        useState(false);
    const [isMetadataHidden, setIsMetadataHidden] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [spectraModalVisible, setSpectraModalVisible] = useState(false);
    const [detailsModalVisible, setDetailsModalVisible] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isSpectrumModalDisabled, setIsSpectrumModalDisabled] =
        useState(false);

    const [dataIds, setDataIds] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Scans-itemsPerPage", true) || 100
    );

    const [exportScans, setExportScans] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const [options, setOptions] = useState({
        hideMetadata: false,
        hideSpectralData: true,
        hideReferenceData: false,
        hideExternalReferenceData: false,
    });

    const [editMode, setEditMode] = useState(false);
    const [organizationUsersLookup, setOrganizationUsersLookup] = useState([]);

    const [syncScansLoading, setSyncScansLoading] = useState(false);
    const { t } = useTranslation();

    const DETAILS_FIELDS = [
        {
            label: t("createdBy"),
            name: "collectedBy",
            type: FIELD_TYPES.Text,
        },
        {
            label: t("onBehalfOf"),
            name: "createdBy",
            type: FIELD_TYPES.Dropdown,
            options: organizationUsersLookup,
            disabled:
                editMode &&
                isAuthorized(
                    [ROLES.admin, ROLES.partnerAdmin, ROLES.support],
                    getRoles()
                ),
            placeholder: t("user"),
        },
        {
            label: t("capturedAt"),
            name: "capturedAt",
            type: FIELD_TYPES.DateTime,
        },
        {
            label: t("materialName"),
            name: "materialName",
            type: FIELD_TYPES.Text,
        },
        { label: t("scannerId"), name: "scanner", type: FIELD_TYPES.Text },
        { label: t("modelName"), name: "modelName", type: FIELD_TYPES.Text },
        {
            label: t("lotName"),
            name: "lot",
            type: FIELD_TYPES.Dropdown,
            options: lotsLookup.filter((lot) => lot.materialId === materialId),
            disabled: editMode,
            placeholder: t("lot"),
            isClearable: true,
        },
        {
            label: t("supplierName"),
            name: "supplier",
            type: FIELD_TYPES.Dropdown,
            options: suppliersLookup,
            disabled: editMode,
            placeholder: t("supplier"),
            isClearable: true,
        },
        { label: t("latitude"), name: "latitude", type: FIELD_TYPES.Text },
        { label: t("longitude"), name: "longitude", type: FIELD_TYPES.Text },
        { label: t("scanTime"), name: "scanTime", type: FIELD_TYPES.Text },
    ];

    const OPTIONS_LIST = [
        {
            label: t("hideMetadata"),
            name: "hideMetadata",
        },
        {
            label: t("hideSpectralData"),
            name: "hideSpectralData",
        },
        {
            label: t("hideReferenceData"),
            name: "hideReferenceData",
        },
        {
            label: t("hideExternalReferenceData"),
            name: "hideExternalReferenceData",
        },
    ];

    const xAxisOptions = [
        {
            label: `${t("wavenumber")} (cm⁻¹)`,
            value: "wavenumber",
        },
        {
            label: `${t("wavelength")} (nm)`,
            value: "wavelength",
        },
    ];

    const yAxisOptions = [
        {
            label: t("reflectance"),
            value: "reflectance",
        },
        {
            label: `${t("absorbance")} (a.u)`,
            value: "absorbance",
        },
    ];

    const SPECTRA_OPTIONS = [
        {
            element: (closeOptionsButton) => (
                <div>
                    <div className="border-bottom">
                        <div className="px-3 py-1">X-axis</div>
                    </div>
                    <div className=" m-2">
                        <VerticalRadioButtons
                            name={"xAxis"}
                            options={xAxisOptions}
                            onChangeHandler={(e) => {
                                spectraRadioHandler(
                                    e,
                                    setXAxisCheckedReference,
                                    isWavenumber,
                                    setIsWavenumber
                                );
                                closeOptionsButton();
                                gridApi.showLoadingOverlay();
                            }}
                            checkedReference={xAxisCheckedReference.value}
                        />
                    </div>
                </div>
            ),
        },
        {
            element: (closeOptionsButton) => (
                <div>
                    <div className="border-bottom">
                        <div className="px-3 py-1">Y-axis</div>
                    </div>
                    <div className="m-2">
                        <VerticalRadioButtons
                            name={"yAxis"}
                            options={yAxisOptions}
                            onChangeHandler={(e) => {
                                spectraRadioHandler(
                                    e,
                                    setYAxisCheckedReference,
                                    isReflectance,
                                    setIsReflectance
                                );
                                closeOptionsButton();
                                gridApi.showLoadingOverlay();
                            }}
                            checkedReference={yAxisCheckedReference.value}
                        />
                    </div>
                </div>
            ),
        },
    ];

    const dropdownButtonOptions = [
        {
            icon: "file-download",
            label: t("export"),
            callback: () => setExportScans(true),
        },
        {
            icon: "file-code",
            label: t("exportXml"),
            callback: () => exportXML(),
        },
        {
            icon: "brain fa-xs",
            label: t("eigenvector"),
            callback: () => {
                setIsWavenumber(false);
                setExportScans(true);
            },
        },
    ];

    useEffect(() => {
        if (!isEmpty(selectedRows)) {
            if (selectedRows.length < SPECTRUM_POINTS_BOUNDARY)
                setSelectedSpectrumPoints(
                    CreateSpectrumPoints(getSelectedRowsData(), true)
                );
            else setIsSpectrumModalDisabled(true);
        }
    }, [selectedRows, data]);

    useEffect(() => {
        if (data && selectedRows && gridApi) {
            let nodes = [];
            // remove the unselected
            gridApi.getSelectedNodes().forEach((node) => {
                if (!selectedRows.includes(node.id)) {
                    node.setSelected(false);
                    nodes.push(node);
                }
            });

            // mark as selected
            for (const id of selectedRows) {
                const node = gridApi.getRowNode(id);
                if (node) {
                    node.setSelected(true);
                    nodes.push(node);
                }
            }
        }
    }, [selectedRows]);

    useEffect(() => {
        if (gridApi) {
            gridApi.redrawRows({ rowNodes: gridApi.getSelectedNodes() });
        }
    }, [data?.scanDetails]);

    useEffect(() => {
        if (data?.headers) generateData(data, modifiedDataReference);
    }, [data, modifiedDataReference, externalDataReference]);

    useEffect(() => {
        hideorShowColumn("metadata", !isMetadataHidden);
    }, [isMetadataHidden, gridColumnApi]);

    useEffect(() => {
        hideorShowColumn("reference", !isReferenceHidden);
    }, [isReferenceHidden, gridColumnApi]);

    useEffect(() => {
        hideorShowColumn("externalReference", !isExternalReferenceHidden);
    }, [isExternalReferenceHidden, gridColumnApi]);

    useEffect(() => {
        hideorShowColumn("data", !isDataHidden);
    }, [isDataHidden, gridColumnApi]);

    const spectraRadioHandler = (e, referenceSetter, flag, flagSetter) => {
        flagSetter(!flag);
        referenceSetter(e.target);
    };

    const handlePageClick = (index) => {
        if (index !== currentPage) {
            setCurrentPage(index);
            getScans(index - 1, itemsPerRequest);
            cancelSelection();
        }
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setCurrentPage(1);
        setObject("Scans-itemsPerPage", itemsPerPage, true);
        getScans(0, itemsPerPage);
        cancelSelection();
    };

    const getSelectedRowsData = () => {
        const selectedDictionary = {};
        selectedRows.forEach((row) => {
            selectedDictionary[row] = row;
        });

        let selectedData = cloneDeep(data);
        selectedData = { ...selectedData, ids: [], rows: [] };

        data.ids.forEach((row, index) => {
            if (selectedDictionary[row?.id]) {
                selectedData.ids.push(row);
                selectedData.rows.push(data.rows[index]);
            }
        });

        return selectedData;
    };

    const hideorShowColumn = (key, state) => {
        if (gridColumnApi && gridApi) {
            const colDef = gridApi.getColumnDefs();

            const group = colDef.find((c) => c.field === key);
            const children = group?.children?.map((c) => c.colId);
            if (children) {
                gridColumnApi.setColumnsVisible(children, state);
            }
        }
    };

    const generateData = (retrievedData, retrievedModifiedDataReference) => {
        if (retrievedData && retrievedModifiedDataReference) {
            setIsLoading(true);
            concatDataWithReference(
                retrievedData,
                retrievedModifiedDataReference,
                externalDataReference
            );
        }
    };

    const classificationValueFormatter = (params) => {
        const nodeId = params?.node.id.split("_")[0];
        const headerName = params.colDef.headerName;
        const prediction =
            data.scanDetails[nodeId]?.predictions[headerName] || {};
        const type = prediction.type;

        return type === MODEL_PROCESSING_TYPES.Classification?.value
            ? prediction.label
            : params.value;
    };

    const getDropdownValues = (
        retrievedData,
        index,
        metadataKeys,
        dropdownValues
    ) => {
        const DROPDOWNS = ["scanner", "createdBy"];
        const rowMetadata = [];
        for (const key of metadataKeys) {
            rowMetadata.push(retrievedData.metadata[key][index] || "--");
            // add values in dropdown
            if (DROPDOWNS.includes(key) && retrievedData.metadata[key][index]) {
                if (!dropdownValues[key]) dropdownValues[key] = {};
                dropdownValues[key][retrievedData.metadata[key][index]] = true;
            }
        }
        return rowMetadata;
    };

    const concatDataWithReference = (
        retrievedData,
        reference,
        externalReference
    ) => {
        const copiedData = JSON.parse(JSON.stringify(retrievedData)); // Deep clone so we dont modify actual data
        const metadataKeys = Object.keys(retrievedData.metadata);
        const dropdownValues = {};

        const headers = [
            "Sample Name",
            ...metadataKeys,
            ...copiedData.headers,
            ...(reference.headers.length > 0 ? reference.headers : []),
            ...(externalReference.headers.length > 0
                ? externalReference.headers
                : []),
        ];

        copiedData.rows.forEach((row, index) => {
            const referenceRow =
                reference.rows[index] ||
                new Array(reference?.headers?.length).fill("--");

            const rowMetadata = getDropdownValues(
                retrievedData,
                index,
                metadataKeys,
                dropdownValues
            );

            const externalReferenceRow =
                externalReference.rows[index] ||
                new Array(externalReference.headers.length).fill("--");

            copiedData.rows[index] = [
                ...row.slice(0, 1),
                ...rowMetadata,
                ...row.slice(1),
                ...(referenceRow ? referenceRow : []),
                ...(externalReferenceRow ? externalReferenceRow : []),
                index,
                `${copiedData.ids[index].id}`,
            ];
        });

        setHeaders(headers);
        setMixedData([...copiedData.rows]);
        setHeaders([...headers]);
        setDataIds(copiedData.ids);
        setIsLoading(false);
    };

    const cloudDelete = () => {
        const selectedRowsKeys = {};
        gridApi.getSelectedNodes().forEach((row) => {
            const _index = row.data[row?.data?.length - 2];
            const scan = dataIds[_index];

            if (!selectedRowsKeys[scan.id]) {
                selectedRowsKeys[scan.id] = [];
            }

            selectedRowsKeys[scan.id].push(scan.key);
        });
        return Object.keys(selectedRowsKeys);
    };

    const afterDeletingScans = (selectedRowsIds) => {
        if (mixedData.length === selectedRowsIds.length) {
            const lastPage = Math.ceil(scans?.totalCount / itemsPerRequest);
            if (lastPage === currentPage) {
                if (lastPage !== 1) {
                    getScans(currentPage - 2, itemsPerRequest);
                    setCurrentPage((prevState) => prevState - 1);
                }
            } else {
                getScans(currentPage - 1, itemsPerRequest);
            }
        }
    };

    const handleDelete = async () => {
        return new Promise((resolve, reject) => {
            if (gridApi) {
                try {
                    const selectedRowsIds = cloudDelete();
                    const count = Object.keys(selectedRowsIds).length;
                    ScansAPI.deleteScans(selectedRowsIds).then(() => {
                        SuccessAlert(t("deleteScansSuccessMessage", { count }));
                        LogCapture.info(
                            `Scan measurements deleted with count: ${count}`
                        );
                        setMixedData((prevState) => {
                            return prevState.filter((item) => {
                                if (
                                    !selectedRowsIds.includes(
                                        item[item.length - 1]
                                    )
                                )
                                    return item;
                            });
                        });
                        afterDeletingScans(selectedRowsIds);
                        cancelSelection();
                        resolve();
                    });
                } catch (e) {
                    reject(e);
                }
            }
        });
    };

    useEffect(() => {
        cancelSelection();
    }, [appliedFilter]);

    const cancelSelection = () => {
        if (gridApi) {
            gridApi.deselectAll();
            setSelectedRows([]);
        }
    };

    const exportTable = async (columns, format) => {
        setIsExporting(true);
        const result = await scansApi.exportScans(
            {
                ...mapScanFilter(),
                columns,
                selectedRows,
                isWavenumber,
                isReflectance,
            },
            format
        );
        if (result?.url) handleExternalLink(result.url);
        setExportScans(false);
        setIsExporting(false);
    };

    const exportXML = async () => {
        setIsExporting(true);
        const result = await scansApi.exportScans(
            {
                ...mapScanFilter(),
                columns: {
                    sampleName: true,
                    capturedAt: true,
                    deviceId: true,
                    onBehalfOf: true,
                    createdBy: true,
                    lotName: true,
                    note: true,
                    results: true,
                },
            },
            EXPORT_FORMATS.xml
        );
        if (result?.url) handleExternalLink(result?.url);
        setIsExporting(false);
    };

    const applyOptions = (retrievedOptions) => {
        const {
            hideMetadata,
            hideSpectralData,
            hideReferenceData,
            hideExternalReferenceData,
        } = retrievedOptions;

        setIsMetadataHidden(hideMetadata || false);
        setIsDataHidden(hideSpectralData || false);
        setIsReferenceHidden(hideReferenceData || false);
        setIsExternalReferenceHidden(hideExternalReferenceData || false);
    };

    const onGridReady = (params) => {
        setGridApi(params.api);
        setGridColumnApi(params.columnApi);
        params.api.showLoadingOverlay();
    };

    const onSelectionChanged = () => {
        const retrievedSelectedRows = gridApi
            .getSelectedNodes()
            .map((node) => node.id);

        if (!isEqual(retrievedSelectedRows, selectedRows)) {
            setSelectedRows(retrievedSelectedRows);
        }
    };

    const handleRowClicked = (params) => {
        setDetailsModalVisible(true);
        const id = params.node.id.split("_")[0];
        setSelectedRow(id);
    };

    const isMeasurement = (index) => {
        if (data && data.ids) {
            const id = data.ids[index]?.id;
            if (data.scanDetails[id]) {
                return {
                    isMeasurement: data.scanDetails[id].isMeasurement,
                    outlier: data.scanDetails[id].outlier,
                };
            }
        }
    };

    const mainCellRenderer = ({ value, markedAsMeasurements, rowIndex }) => {
        const rowData = markedAsMeasurements(rowIndex);
        return (
            <div className="flex items-center">
                <span className="truncate">{value}</span>

                <span className="mark-success gap-1.5 items-center">
                    {rowData.outlier && (
                        <Tippy
                            content={t("outlierFlag")}
                            placement="top"
                            animation="scale"
                        >
                            <span className="flex items-center gap-0.5 text-xxs text-red-700">
                                <i className="fas fa-exclamation-triangle fa-sm" />
                                <span>{t("outlier")}</span>
                            </span>
                        </Tippy>
                    )}

                    {rowData.isMeasurement && (
                        <Tippy
                            content={t("markedAsMeasurementsFlag")}
                            placement="top"
                            animation="scale"
                        >
                            <i className="far fa-check-circle fa-sm text-green-600" />
                        </Tippy>
                    )}
                </span>
            </div>
        );
    };

    const getSelectedRows = () => {
        for (const id of selectedRows) {
            const node = gridApi.getRowNode(id);
            if (node) {
                node.setSelected(true);
            }
        }
    };

    const syncScans = async () => {
        setSyncScansLoading(true);
        const result = await scansApi.syncScans(selectedRows);
        if (result === selectedRows.length) {
            SuccessAlert(t("scanSyncMessage"));
        }
        cancelSelection();
        setSyncScansLoading(false);
    };

    const renderSyncButton = () => {
        if (
            isAuthorized(
                [ROLES.partnerAdmin, ROLES.support, ROLES.admin],
                getRoles()
            ) &&
            scans?.allowSync
        )
            return (
                <button
                    onClick={syncScans}
                    disabled={syncScansLoading || selectedRows.length === 0}
                    className="default-orange-btn flex gap-1.5 items-center"
                >
                    {syncScansLoading ? (
                        <span className="animate-spin">
                            <i className="fas fa-sync"></i>
                        </span>
                    ) : (
                        <i className="fa fa-sync"></i>
                    )}
                    {`${syncScansLoading ? t("syncing") : t("sync")} (${
                        selectedRows.length
                    })`}
                </button>
            );
    };

    return (
        <>
            <div
                className={`tabs-content-items flex flex-col mb-5 ${
                    visible ? "show" : ""
                }`}
                id="data-content-wrapper"
            >
                <div className="steps-btn flex items-center justify-end mb-4">
                    <div className="flex items-center">
                        {renderSyncButton()}
                        <OptionsDropdown
                            optionsList={SPECTRA_OPTIONS}
                            title={t("spectraDataOptions")}
                            clearBtn={false}
                            gridApi={gridApi}
                        />
                        <OptionsDropdown
                            optionsList={OPTIONS_LIST}
                            options={options}
                            setOptions={setOptions}
                            applyOptions={applyOptions}
                        />
                        <DropdownButton
                            buttonName={
                                isExporting
                                    ? t("requesting")
                                    : `${t("export")}${
                                          selectedRows.length > 0
                                              ? ` (${selectedRows.length})`
                                              : ""
                                      }`
                            }
                            disabled={isExporting}
                            options={dropdownButtonOptions}
                        />
                    </div>
                </div>

                <LoadingOverlay active={isLoading} text="Loading...">
                    <div className="grid shadow sm:rounded-lg ag-grid-height">
                        <div
                            className="ag-theme-alpine text-left"
                            style={{ height: "100%" }}
                        >
                            <AgGridReact
                                onGridReady={onGridReady}
                                onRowClicked={handleRowClicked}
                                rowSelection="multiple"
                                suppressRowTransform={true}
                                getRowNodeId={(data) => data[data.length - 1]}
                                defaultColDef={{
                                    lockPosition: true,
                                }}
                                rowMultiSelectWithClick={true}
                                suppressRowClickSelection={true}
                                onSelectionChanged={onSelectionChanged}
                                rowData={mixedData}
                                frameworkComponents={{
                                    actionCellRenderer: mainCellRenderer,
                                }}
                                onRowDataChanged={getSelectedRows}
                            >
                                <AgGridColumn
                                    field={"0"}
                                    headerName="Sample Name"
                                    pinned={true}
                                    width={350}
                                    maxWidth={700}
                                    resizable={true}
                                    headerCheckboxSelection={true}
                                    headerCheckboxSelectionFilteredOnly={true}
                                    checkboxSelection={true}
                                    cellClass="text-gray-500"
                                    cellRenderer="actionCellRenderer"
                                    cellRendererParams={{
                                        markedAsMeasurements: isMeasurement,
                                    }}
                                    cellStyle={(params) => {
                                        const val =
                                            data?.scanDetails[params.node.id];
                                        if (val?.outlier) {
                                            return {
                                                backgroundColor: "#ef44443b",
                                            };
                                        } else {
                                            return null;
                                        }
                                    }}
                                ></AgGridColumn>
                                {data?.metadata &&
                                    Object.keys(data.metadata).length > 0 && (
                                        <AgGridColumn
                                            field="metadata"
                                            headerName="Metadata"
                                            colId="metadata"
                                        >
                                            {Object.keys(data.metadata).map(
                                                (col, index) => (
                                                    <AgGridColumn
                                                        key={col}
                                                        colId={col}
                                                        field={(
                                                            index + 1
                                                        ).toString()}
                                                        headerName={
                                                            METADATA_COLUMNS[
                                                                col
                                                            ] || col
                                                        }
                                                        cellRenderer={(
                                                            params
                                                        ) =>
                                                            col ===
                                                            CAPTURED_AT_COL
                                                                ? shortDateTime(
                                                                      params.value
                                                                  )
                                                                : params.value
                                                        }
                                                    ></AgGridColumn>
                                                )
                                            )}
                                        </AgGridColumn>
                                    )}
                                <AgGridColumn
                                    headerName="Spectral Data"
                                    field="data"
                                    colId="data"
                                >
                                    {data?.headers &&
                                        data.headers.map((col, index) => (
                                            <AgGridColumn
                                                key={index}
                                                hide={isDataHidden}
                                                field={(
                                                    Object.keys(data.metadata)
                                                        ?.length +
                                                    index +
                                                    1
                                                ).toString()}
                                                headerName={
                                                    isNaN(col)
                                                        ? col
                                                        : col.toString()
                                                }
                                                cellRenderer={(params) =>
                                                    !isNaN(params.value) &&
                                                    (+params.value).toFixed(3)
                                                }
                                            ></AgGridColumn>
                                        ))}
                                </AgGridColumn>
                                {modifiedDataReference?.headers?.length > 0 && (
                                    <AgGridColumn
                                        field="reference"
                                        headerName={t("results")}
                                        colId="reference"
                                    >
                                        {modifiedDataReference.headers.map(
                                            (col, index) => (
                                                <AgGridColumn
                                                    key={col}
                                                    headerName={col}
                                                    valueFormatter={
                                                        classificationValueFormatter
                                                    }
                                                    field={(
                                                        Object.keys(
                                                            data.metadata
                                                        )?.length +
                                                        data.headers?.length +
                                                        index +
                                                        1
                                                    ).toString()}
                                                ></AgGridColumn>
                                            )
                                        )}
                                    </AgGridColumn>
                                )}
                                {externalDataReference?.headers.length > 0 && (
                                    <AgGridColumn
                                        field="externalReference"
                                        headerName="External Reference Data"
                                        colId="externalReference"
                                    >
                                        {externalDataReference.headers.map(
                                            (col, index) => (
                                                <AgGridColumn
                                                    key={col}
                                                    headerName={col}
                                                    field={(
                                                        Object.keys(
                                                            data.metadata
                                                        ).length +
                                                        data.headers.length +
                                                        modifiedDataReference
                                                            .headers.length +
                                                        index +
                                                        1
                                                    ).toString()}
                                                ></AgGridColumn>
                                            )
                                        )}
                                    </AgGridColumn>
                                )}
                            </AgGridReact>
                        </div>
                    </div>
                </LoadingOverlay>

                <div className="pt-3">
                    <Pagination
                        currentPage={currentPage}
                        setCurrentPage={handlePageClick}
                        totalCount={scans?.totalCount || 0}
                        sizePerPage={itemsPerRequest}
                        pageSizeFactor={10}
                        handleItemsPerPage={handleItemsPerPage}
                        width="70px"
                    />
                </div>

                <div className="steps-btn flex items-center justify-end mt-4">
                    <button
                        disabled={!selectedRows.length}
                        onClick={() => setSpectraModalVisible(true)}
                        className={`default-orange-outline-btn ${
                            !selectedRows.length ? "disabled" : ""
                        }`}
                    >
                        <span className="mr-1">
                            <Trans i18nKey="plotSpectra">Plot Spectra</Trans>
                        </span>
                        {selectedRows.length > 0 && (
                            <span>({selectedRows.length})</span>
                        )}
                    </button>
                    <button
                        disabled={!selectedRows.length}
                        onClick={() => setDeleteModalVisible(true)}
                        className="default-orange-btn"
                    >
                        <i className="fas fa-trash-alt" />
                        <span className="ml-2 mr-1">
                            <Trans i18nKey="delete">Delete</Trans>
                        </span>
                        {selectedRows.length > 0 && (
                            <span>({selectedRows.length})</span>
                        )}
                    </button>
                    <button
                        onClick={cancelSelection}
                        className="default-transparent-btn"
                    >
                        <Trans i18nKey="cancelSelection">
                            Cancel Selection
                        </Trans>
                    </button>
                </div>
            </div>

            <MeasurementDeleteModal
                modalButton={deleteModalVisible}
                modalButtonSetter={setDeleteModalVisible}
                submitAction={handleDelete}
                title={t("deleteScansTitle")}
                message={t("deleteMeasurementsMessage", {
                    count: selectedRows.length,
                })}
            />

            <ScanDetailsModal
                isVisible={detailsModalVisible}
                setIsVisible={setDetailsModalVisible}
                scanId={selectedRow}
                fields={DETAILS_FIELDS}
                entityName="Scan"
                showSpectralData={true}
                setMixedData={setMixedData}
                gridApi={gridApi}
                cancelSelection={cancelSelection}
                editMode={editMode}
                setEditMode={setEditMode}
                setOrganizationUsersLookup={setOrganizationUsersLookup}
            />

            <BaseModal
                isVisible={spectraModalVisible}
                setIsVisible={setSpectraModalVisible}
                title={t("spectrum")}
            >
                <SpectrumChart
                    visible={true}
                    isSpectrumDisabled={isSpectrumModalDisabled}
                    renderSwitch={false}
                    enableSelectionButtons={false}
                    points={selectedSpectrumPoints}
                    hideBackground={true}
                    isWavenumber={isWavenumber}
                    isReflectance={isReflectance}
                />
            </BaseModal>

            <ExportScansModal
                isVisible={exportScans}
                setIsVisible={setExportScans}
                isResultsHidden={isReferenceHidden}
                isSpectralDataHidden={isDataHidden}
                isMetadataHidden={isMetadataHidden}
                submit={exportTable}
                isSubmitting={isExporting}
                rowsCount={selectedRows.length || scans.totalCount}
                selectedRows={selectedRows}
                isReflectance={isReflectance}
            />
        </>
    );
};

export default ScansDataTable;
